import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Package, Calendar as CalendarIcon, Loader2, CreditCard, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useProfileValidation } from "@/hooks/use-profile-validation";
import { ProfileCompletionModal } from "@/components/ProfileCompletionModal";

interface Box {
  id: string;
  box_number: string;
  name: string;
  room: string;
  location_status: string;
  hasScheduledPickup?: boolean;
}

const SchedulePickupConnected = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedBoxIds, setSelectedBoxIds] = useState<string[]>([]);
  const [pickupDate, setPickupDate] = useState<Date>();
  const [timeSlot, setTimeSlot] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [missingFields, setMissingFields] = useState<{ field: string; label: string }[]>([]);
  const [isPrePaid, setIsPrePaid] = useState(false);
  
  const { validateProfile, isLoading: isValidating } = useProfileValidation();

  useEffect(() => {
    fetchBoxes();
  }, []);

  const fetchBoxes = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      // Fetch user profile to check pre-paid status
      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_type')
        .eq('id', user.id)
        .single();

      // Check if user is pre-paid (Shopify migration, in-store purchase, etc.)
      const prePaidTypes = ['shopify_import', 'in_store_purchase', 'direct_storage_purchase'];
      setIsPrePaid(prePaidTypes.includes(profile?.onboarding_type || ''));

      // Fetch boxes that are at home
      const { data: boxesData, error: boxesError } = await supabase
        .from('boxes')
        .select('id, box_number, name, room, location_status')
        .eq('user_id', user.id)
        .eq('location_status', 'home')
        .order('created_at', { ascending: false });

      if (boxesError) throw boxesError;

      // Check which boxes have scheduled pickups
      const { data: upcomingShipments } = await supabase
        .from('shipments')
        .select('box_ids')
        .eq('user_id', user.id)
        .eq('type', 'to_storage')
        .eq('status', 'scheduled')
        .gte('pickup_date', new Date().toISOString());

      const scheduledBoxIds = new Set(
        upcomingShipments?.flatMap(s => s.box_ids) || []
      );

      const boxesWithScheduleStatus = (boxesData || []).map(box => ({
        ...box,
        hasScheduledPickup: scheduledBoxIds.has(box.id)
      }));

      setBoxes(boxesWithScheduleStatus);

      // Fetch item counts for each box
      if (boxesData) {
        const counts: Record<string, number> = {};
        await Promise.all(
          boxesData.map(async (box) => {
            const { count } = await supabase
              .from('items')
              .select('*', { count: 'exact', head: true })
              .eq('box_id', box.id);
            counts[box.id] = count || 0;
          })
        );
        setItemCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast({
        title: "Error",
        description: "Failed to load boxes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleBox = (boxId: string) => {
    setSelectedBoxIds(prev => 
      prev.includes(boxId) 
        ? prev.filter(id => id !== boxId)
        : [...prev, boxId]
    );
  };

  const totalCost = selectedBoxIds.length * 9.99;

  const handleCheckout = async () => {
    if (selectedBoxIds.length === 0) {
      toast({
        title: "No boxes selected",
        description: "Please select at least one box for pickup",
        variant: "destructive",
      });
      return;
    }

    if (!pickupDate) {
      toast({
        title: "No pickup date selected",
        description: "Please select a pickup date",
        variant: "destructive",
      });
      return;
    }

    if (!timeSlot) {
      toast({
        title: "No time window selected",
        description: "Please select a pickup time window",
        variant: "destructive",
      });
      return;
    }

    // Validate profile before checkout
    const validation = await validateProfile();
    
    if (!validation.isComplete) {
      setMissingFields(validation.missingFields);
      setShowProfileModal(true);
      return;
    }

    setIsLoading(true);
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        throw new Error("You must be logged in to proceed");
      }

      const { data, error } = await supabase.functions.invoke('create-pickup-checkout', {
        body: { 
          boxIds: selectedBoxIds,
          pickupDate: pickupDate.toISOString(),
          timeSlot: timeSlot,
        },
      });

      if (error) throw error;

      if (data?.url) {
        // Redirect to Stripe Checkout (Stripe will redirect back to success_url)
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: "Checkout Failed",
        description: error instanceof Error ? error.message : "Failed to start checkout",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold text-primary">Endless Storage</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="shadow-elegant">
          <CardHeader>
            <CardTitle className="text-2xl">Schedule a Pickup</CardTitle>
            <CardDescription>
              Select boxes to store and choose a pickup date
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Box Selection */}
            <div>
              <h3 className="font-semibold mb-4 text-lg">Select Boxes ({selectedBoxIds.length} selected)</h3>
              {boxes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No boxes available for pickup. All your boxes are either already in storage or in transit.
                </p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {boxes.map((box) => (
                    <div
                      key={box.id}
                      className={cn(
                        "flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all",
                        selectedBoxIds.includes(box.id)
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                      onClick={() => toggleBox(box.id)}
                    >
                      <Checkbox
                        checked={selectedBoxIds.includes(box.id)}
                        onCheckedChange={() => toggleBox(box.id)}
                      />
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {box.name}
                          {box.hasScheduledPickup && (
                            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full border border-primary/20">
                              Already Scheduled
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {box.box_number} · {itemCounts[box.id] || 0} items · {box.room}
                        </div>
                      </div>
                      {!isPrePaid && (
                        <div className="text-sm font-semibold text-primary">
                          $9.99/mo
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Date Selection */}
            <div>
              <h3 className="font-semibold mb-4 text-lg">Pickup Date</h3>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !pickupDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {pickupDate ? format(pickupDate, "PPP") : "Select pickup date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={pickupDate}
                    onSelect={setPickupDate}
                    disabled={(date) => {
                      const threeDaysFromNow = new Date();
                      threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
                      threeDaysFromNow.setHours(0, 0, 0, 0);
                      return date < threeDaysFromNow || date.getDay() === 0;
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Slot Selection */}
            <div>
              <h3 className="font-semibold mb-4 text-lg">Pickup Time Window</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['Morning (9 AM - 12 PM)', 'Afternoon (12 PM - 3 PM)', 'Evening (3 PM - 6 PM)'].map((slot) => (
                  <Button
                    key={slot}
                    variant={timeSlot === slot ? "default" : "outline"}
                    onClick={() => setTimeSlot(slot)}
                  >
                    {slot}
                  </Button>
                ))}
              </div>
            </div>

            {/* Pricing Summary - Different for pre-paid vs regular users */}
            {selectedBoxIds.length > 0 && (
              isPrePaid ? (
                <div className="p-6 border-2 border-green-500/20 rounded-lg bg-green-50 dark:bg-green-950/20">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
                        Pre-Paid Storage Ready
                      </h3>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {selectedBoxIds.length} box{selectedBoxIds.length > 1 ? 'es' : ''} selected for pickup
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-green-800 dark:text-green-200">
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-600" />
                      Your storage is already paid for
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-600" />
                      No additional charges for this pickup
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-600" />
                      Climate-controlled secure storage included
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-600" />
                      Free delivery when you need items back
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="p-6 border-2 border-primary/20 rounded-lg bg-primary/5">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Monthly Total</h3>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">${totalCost.toFixed(2)}</div>
                      <div className="text-sm text-muted-foreground">
                        {selectedBoxIds.length} box{selectedBoxIds.length > 1 ? 'es' : ''} × $9.99/month
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      Secure climate-controlled storage
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      24/7 access to your inventory
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      Free delivery when you need items back
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                      Cancel anytime
                    </li>
                  </ul>
                </div>
              )
            )}

            {/* Action Button */}
            <Button
              className="w-full bg-gradient-primary"
              size="lg"
              onClick={handleCheckout}
              disabled={isLoading || isValidating || selectedBoxIds.length === 0 || !pickupDate || !timeSlot}
            >
              {isLoading || isValidating ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {isValidating ? "Checking profile..." : "Processing..."}
                </>
              ) : (
                <>
                  {isPrePaid ? (
                    <>
                      <Package className="h-5 w-5 mr-2" />
                      Schedule Pickup
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Proceed to Checkout
                    </>
                  )}
                </>
              )}
            </Button>
            
            <ProfileCompletionModal
              isOpen={showProfileModal}
              onClose={() => setShowProfileModal(false)}
              missingFields={missingFields}
            />

            {isPrePaid ? (
              <p className="text-xs text-center text-muted-foreground">
                Your storage has been pre-paid. We'll schedule your pickup and send you shipping labels.
              </p>
            ) : (
              <p className="text-xs text-center text-muted-foreground">
                Your subscription will be managed through Stripe. You can cancel anytime from your account settings.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SchedulePickupConnected;
